import { Colors, Text } from '@amelio/ui-library';

import { useAmelioTranslations } from '@/hooks';
import { formatTimeOfDay } from '@/utils/dateUtils';

interface AutosaveStatusProps {
    isSaving: boolean;
    /** When the last save of this visit succeeded. Nothing is said before the first one. */
    lastSavedAt: Date | null;
    /** Whether the last save failed; a later success clears it. */
    hasFailed: boolean;
    language: string;
    /** Said instead of the save outcome when the section cannot save at all. */
    unavailableMessage?: string;
    dataTestId: string;
}

/**
 * The quiet status line under a plan section that saves on its own: "Saving..." while a save is in
 * flight, then the outcome of the last save — its failure, or its time, which stays until the next
 * save replaces it.
 *
 * Only the outcome sits in the announced region. Some sections save on every click (the agenda
 * does), so announcing the in-flight state too would read two messages per click; it is shown on
 * screen only, while the region keeps its last outcome, hidden from view but unchanged, so nothing
 * is re-announced until the outcome itself changes. The region is rendered at all times so it
 * exists before its text changes: a live region inserted together with its content is often missed.
 */
export const AutosaveStatus = ({
    isSaving,
    lastSavedAt,
    hasFailed,
    language,
    unavailableMessage,
    dataTestId,
}: AutosaveStatusProps) => {
    const { t } = useAmelioTranslations();

    const getOutcome = (): string => {
        if (unavailableMessage) {
            return unavailableMessage;
        }
        if (hasFailed) {
            return t('thematicMeetings.plan.autosaveFailed');
        }
        if (lastSavedAt) {
            return t('thematicMeetings.plan.autosavedAt', {
                time: formatTimeOfDay(lastSavedAt, language),
            });
        }
        return '';
    };

    const isSavingShown = isSaving && !unavailableMessage;

    return (
        <div data-testid={dataTestId}>
            {isSavingShown && (
                <Text
                    size={'small'}
                    color={Colors.BASE_500}
                >
                    {t('thematicMeetings.plan.autosaving')}
                </Text>
            )}

            <div
                role={'status'}
                className={isSavingShown ? 'sr-only' : undefined}
            >
                <Text
                    size={'small'}
                    color={Colors.BASE_500}
                >
                    {getOutcome()}
                </Text>
            </div>
        </div>
    );
};
